import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { framesService } from '@/features/frames-to-order/services/framesService';
import { invalidateFrameStatusQueries } from '@/shared/utils/queryUtils';
import type { FrameFilters, MarkAsReceivedInput } from '@/shared/types/domain.types';

/**
 * Query key factory for receiving
 */
export const receivingKeys = {
  all: ['receiving'] as const,
  orderedFrames: (filters?: FrameFilters) =>
    [...receivingKeys.all, 'ordered-frames', filters] as const,
  manufacturerGroups: (filters?: FrameFilters) =>
    [...receivingKeys.all, 'manufacturer-groups', filters] as const,
};

/**
 * Hook to fetch ordered frames (status = ORDERED)
 * These are frames ready to be marked as received
 */
export function useOrderedFrames(filters?: FrameFilters) {
  // Always filter to ORDERED status
  const orderedFilters: FrameFilters = {
    ...filters,
    status: ['ORDERED'],
  };

  return useQuery({
    queryKey: receivingKeys.orderedFrames(filters),
    queryFn: () => framesService.getFrames(orderedFilters),
  });
}

/**
 * Hook to fetch ordered frames grouped by manufacturer
 */
export function useOrderedFramesGroupedByManufacturer(filters?: FrameFilters) {
  return useQuery({
    queryKey: receivingKeys.manufacturerGroups(filters),
    queryFn: () => framesService.getOrderedFramesGroupedByManufacturer(filters),
  });
}

/**
 * Hook to mark frames as received
 * Includes cache invalidation for all related queries
 */
export function useMarkAsReceived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MarkAsReceivedInput) => framesService.markAsReceived(input),
    onSuccess: () => {
      // Use centralized invalidation to update all frame-status-dependent views
      invalidateFrameStatusQueries(queryClient);
    },
  });
}
